import React, { useState } from 'react';
import { 
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { softColors } from "../../constants/colors";
import { styled, alpha } from '@mui/material/styles';
import { useUser } from '../../contexts/UserContext';
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

const ApprovalWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  backgroundColor: alpha(softColors.success, 0.04),
  borderRadius: '4px',
  direction: 'rtl',
  textAlign: 'right'
}));

const Separator = styled('span')({
  margin: '0 8px',
  color: alpha(softColors.text, 0.5),
});

// כפתור מותאם RTL
const RtlButton = styled(Button)(({ theme }) => ({
  '& .MuiButton-startIcon': {
    marginRight: -4,
    marginLeft: 8, // התאמה לכיוון RTL
  }
}));

const VerificationApproval = ({ verification, invoiceId, onVerificationUpdate }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUser();
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.write
  });

  const formatApprovalDate = (date) => {
    try {
      return format(new Date(date), "dd/MM/yy HH:mm", { locale: he });
    } catch (e) {
      return date;
    }
  };

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);

      // שליחה לשרת קודם
      const response = await execute(
        'POST',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/checkInvoices/approveVerification`,
        {
          invoiceId,
          verificationKey: verification.key,
          userEmail: user.email,
          notes: notes.trim()
        }
      );

      console.log('Server response:', response);

      // בדיקה אם יש תשובה תקינה מהשרת
      if (response?.success) {
        const updatedVerification = response?.data?.updatedVerification;
        
        if (updatedVerification) {
          // עדכון הסטייט עם התשובה מהשרת
          onVerificationUpdate(verification.key, updatedVerification);
        } else {
          // אם אין updatedVerification, ניצור אחד מקומי עם הנתונים שלנו
          const manualVerification = {
            ...verification,
            Result: true,
            Passed: true,
            manuallyApproved: true,
            approvedBy: user.email,
            approvalNote: notes.trim(),
            approvedAt: new Date().toISOString()
          };
          onVerificationUpdate(verification.key, manualVerification);
        }
        
        setOpen(false);
        setNotes('');
      } else {
        throw new Error(response?.message || 'Server returned unsuccessful response');
      }
      
    } catch (error) {
      console.error('Error approving verification:', error);
      console.error('Error details:', error.response || error);
      alert('שגיאה באישור הבדיקה. אנא נסה שנית.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (verification.Result || verification.Passed) {
    return verification.manuallyApproved ? (
      <ApprovalWrapper>
        <Typography variant="body2" sx={{ width: '100%', textAlign: 'right' }}>
          <Box component="span" sx={{ color: softColors.success, fontWeight: 500 }}>
            אושר ידנית
          </Box>

          <Separator>•</Separator>
          
          <span> {verification.approvedBy}</span>

          {verification.approvedAt && (
            <>
              <Separator>•</Separator>
              <span>{formatApprovalDate(verification.approvedAt)}</span>
            </>
          )}

          {verification.approvalNote && (
            <>
              <Separator>•</Separator>
              <span>הערה: {verification.approvalNote}</span>
            </>
          )}
        </Typography>
      </ApprovalWrapper>
    ) : null;
  }

  return (
    <Box display="flex" alignItems="center" gap={1} dir="rtl">
      <RtlButton
        variant="outlined"
        size="small"
        onClick={() => setOpen(true)}
        startIcon={<CheckCircleIcon />}
        sx={{
          color: softColors.primary,
          borderColor: softColors.primary,
          '&:hover': {
            borderColor: softColors.primary,
            backgroundColor: alpha(softColors.primary, 0.1),
          },
        }}
      >
        אישור ידני
      </RtlButton>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="sm" 
        fullWidth
        dir="rtl"
        PaperProps={{
          sx: { direction: 'rtl' }
        }}
      >
        <DialogTitle sx={{ pb: 1, textAlign: 'right', direction: 'rtl' }}>
          אישור בדיקה ידני
        </DialogTitle>
        <DialogContent sx={{ pb: 2, textAlign: 'right' }}>
          <Typography variant="body1" sx={{ pt: 1, pb: 2, textAlign: 'right' }}>
            האם לאשר את בדיקת "{verification.Description}" באופן ידני?
          </Typography>
          
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'right' }}>
              משתמש: {user.email}
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              label="הערות (אופציונלי)"
              variant="outlined"
              size="small"
              sx={{ mt: 1, direction: 'rtl' }}
              inputProps={{
                dir: 'rtl',
                style: { textAlign: 'right' }
              }}
              InputLabelProps={{
                style: { right: 20, left: 'auto', transformOrigin: 'right top' }
              }}
              InputProps={{
                sx: { textAlign: 'right', direction: 'rtl' }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          pb: 2, 
          display: 'flex', 
          flexDirection: 'row-reverse', // הפיכת הסדר
          justifyContent: 'flex-end' 
        }}>
          <Button 
            onClick={() => setOpen(false)}
            variant="outlined"
            color="inherit"
            disabled={isSubmitting}
          >
            ביטול
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              mr: 2, // מרווח לימין
              backgroundColor: softColors.success,
              '&:hover': {
                backgroundColor: alpha(softColors.success, 0.8),
              },
            }}
          >
            {isSubmitting ? 'מאשר...' : 'אישור'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerificationApproval;